import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Visual accent (icon + colour) keyed by AttributeCode
const ATTR_ACCENTS = {
  unit:       { icon: '▤', color: '#1a237e' },
  type:       { icon: '✎', color: '#0277bd' },
  difficulty: { icon: '▲', color: '#e65100' },
  co:         { icon: '◎', color: '#2e7d32' },
  bl:         { icon: '✦', color: '#6a1b9a' },
  kp:         { icon: '★', color: '#c62828' },
  po:         { icon: '⬢', color: '#00838f' },
};

/**
 * Tenant-scoped toggles for which attributes are collected when building the
 * Question Bank. Mirrors the QP Methods tab visually: one card per attribute,
 * flipping a switch persists to the backend immediately (optimistic update,
 * revert on failure).
 *
 * Unit / Type / Difficulty are defaulted ON by the backend seed; the rest
 * default OFF and can be turned on if the institute uses them.
 */
function QuestionBankSettingsTab({ tenantId }) {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const loadAttributes = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const res = await api.get(`/qp-settings/qb-attributes/${tenantId}`);
      if (res.data.success) {
        setAttributes(res.data.data || []);
      }
    } catch {
      toast.error('Failed to load question bank settings');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadAttributes();
  }, [loadAttributes]);

  const handleToggle = async (attr) => {
    const next = !attr.IsEnabled;
    setSavingId(attr.SettingID);

    // Optimistic update
    setAttributes((prev) =>
      prev.map((a) =>
        a.SettingID === attr.SettingID ? { ...a, IsEnabled: next } : a
      )
    );

    try {
      await api.put(`/qp-settings/qb-attributes/${attr.SettingID}`, {
        isEnabled: next,
      });
      toast.success(`${attr.AttributeName} ${next ? 'enabled' : 'disabled'}`);
    } catch (err) {
      // Revert on failure
      setAttributes((prev) =>
        prev.map((a) =>
          a.SettingID === attr.SettingID ? { ...a, IsEnabled: !next } : a
        )
      );
      toast.error(err.response?.data?.message || 'Failed to update attribute');
    } finally {
      setSavingId(null);
    }
  };

  const enabledCount = attributes.filter((a) => a.IsEnabled).length;

  return (
    <>
      <div
        className="page-card-header"
        style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}
      >
        <h3>Question Bank Settings</h3>
        <span style={{ fontSize: 13, color: '#616161' }}>
          Choose which attributes will be captured while creating questions in
          the Question Bank. Enabled attributes show up as required fields on
          the question editor. {enabledCount > 0 && (
            <strong style={{ color: '#1a237e' }}>
              {enabledCount} of {attributes.length} enabled
            </strong>
          )}
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          Loading...
        </div>
      ) : attributes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No attributes configured.
        </div>
      ) : (
        <div className="qp-methods-grid">
          {attributes.map((a) => {
            const accent = ATTR_ACCENTS[a.AttributeCode] || {
              icon: '●',
              color: '#1a237e',
            };
            const isSaving = savingId === a.SettingID;
            return (
              <div
                key={a.SettingID}
                className={`qp-method-card ${a.IsEnabled ? 'enabled' : 'disabled'}`}
              >
                <div className="qp-method-card-header">
                  <div
                    className="qp-method-icon"
                    style={{
                      background: `${accent.color}15`,
                      color: accent.color,
                    }}
                  >
                    {accent.icon}
                  </div>
                  <div className="qp-method-title">
                    <div className="qp-method-name">{a.AttributeName}</div>
                    <div className="qp-method-code">{a.AttributeCode.toUpperCase()}</div>
                  </div>
                  <label
                    className="toggle-switch"
                    title={a.IsEnabled ? 'Enabled' : 'Disabled'}
                  >
                    <input
                      type="checkbox"
                      checked={!!a.IsEnabled}
                      disabled={isSaving}
                      onChange={() => handleToggle(a)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>

                <div className="qp-method-description">{a.Description}</div>

                <div className="qp-method-footer">
                  <span
                    className={`badge ${a.IsEnabled ? 'badge-active' : 'badge-inactive'}`}
                  >
                    {a.IsEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  {isSaving && (
                    <span style={{ fontSize: 12, color: '#616161' }}>
                      Saving...
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default QuestionBankSettingsTab;
